import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PanelContent from '@shared/components/PanelContent';
import PanelSkeleton from '@shared/components/PanelSkeleton';
import RetryableError from '@shared/components/RetryableError';
import { ApiRequestError } from '@api/client/apiClient';
import { useDebouncedValue } from '@shared/hooks/useDebouncedValue';
import { useMapStore } from '@shared/hooks/useMapStore';
import { PeriodSelector, usePeriodStore } from '@shared/pricing';
import { useCatalog } from './hooks/useCatalog';
import CatalogSearch from './components/CatalogSearch';
import CatalogList from './components/CatalogList';
import { CATALOG_PAGE_SIZE, CATALOG_SEARCH_DEBOUNCE_MS } from './constants';
import type { ProductType } from './types';

export default function CatalogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search') ?? '';
  const typeFilter = (searchParams.get('type') ?? '') as ProductType | '';
  const coverageFilter = searchParams.get('coverage') ?? '';
  const page = Number(searchParams.get('page') ?? '0');
  const [rowsPerPage, setRowsPerPage] = useState(CATALOG_PAGE_SIZE);
  const selectedProductId = useMapStore((s) => s.selectedProductId);
  const setSelectedProductId = useMapStore((s) => s.setSelectedProductId);
  const setCatalogProducts = useMapStore((s) => s.setCatalogProducts);
  const setFitProductId = useMapStore((s) => s.setFitProductId);
  const setActiveProductsRouteId = useMapStore((s) => s.setActiveProductsRouteId);
  const requestedPeriod = usePeriodStore((s) => s.requestedPeriodMonths);
  const setRequestedPeriod = usePeriodStore((s) => s.setRequestedPeriodMonths);

  const debouncedSearch = useDebouncedValue(search, CATALOG_SEARCH_DEBOUNCE_MS);

  // Task 5 fix: clear activeProductsRouteId so catalog products render on map
  useEffect(() => {
    setActiveProductsRouteId(null);
  }, [setActiveProductsRouteId]);

  const { data, isLoading, error, refetch } = useCatalog({
    search: debouncedSearch || undefined,
    type: typeFilter || undefined,
    coverage: coverageFilter || undefined,
    page,
    size: rowsPerPage,
  });

  // Sync visible catalog products to the map layer
  useEffect(() => {
    setCatalogProducts(data?.items ?? null);
    return () => setCatalogProducts(null);
  }, [data?.items, setCatalogProducts]);

  function handleSearchChange(value: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set('search', value);
      else next.delete('search');
      next.delete('page');
      return next;
    });
  }

  function handleTypeFilterChange(value: ProductType | '') {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set('type', value);
      else next.delete('type');
      next.delete('page');
      return next;
    });
  }

  function handleCoverageFilterChange(values: string[]) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const joined = values.join(',');
      if (joined) next.set('coverage', joined);
      else next.delete('coverage');
      next.delete('page');
      return next;
    });
  }

  const activeFilterCount = (typeFilter ? 1 : 0) + (coverageFilter ? 1 : 0);

  function handleClearFilters() {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('type');
      next.delete('coverage');
      next.delete('page');
      return next;
    });
  }

  function handlePageChange(newPage: number) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (newPage === 0) next.delete('page');
      else next.set('page', String(newPage));
      return next;
    });
  }

  function handleRowsPerPageChange(rpp: number) {
    setRowsPerPage(rpp);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('page');
      return next;
    });
  }

  if (!data && isLoading) return <PanelSkeleton />;

  if (error && !data) {
    const correlationId =
      error instanceof ApiRequestError ? error.apiError.correlationId : undefined;
    return (
      <RetryableError
        message={error.message ?? 'Failed to load catalog'}
        correlationId={correlationId}
        onRetry={() => void refetch()}
      />
    );
  }

  return (
    <PanelContent>
      <div data-testid="page-catalog" className="flex flex-col gap-2">
        <CatalogSearch
          search={search}
          onSearchChange={handleSearchChange}
          typeFilter={typeFilter}
          onTypeFilterChange={handleTypeFilterChange}
          coverageFilter={coverageFilter ? coverageFilter.split(',') : []}
          onCoverageFilterChange={handleCoverageFilterChange}
          activeFilterCount={activeFilterCount}
          onClearFilters={handleClearFilters}
        >
          <PeriodSelector value={requestedPeriod} onChange={setRequestedPeriod} size="small" />
        </CatalogSearch>
        <CatalogList
          products={data?.items ?? []}
          loading={isLoading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalRows={data?.totalItems ?? 0}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSelectProduct={(product) => setSelectedProductId(product.productId)}
          onDoubleClickProduct={(product) => setFitProductId(product.productId)}
          selectedProductId={selectedProductId}
        />
      </div>
    </PanelContent>
  );
}
